import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ContentChildren,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { cloneDeep } from 'lodash';
import { PaginationModel } from 'common/src/models';
import { isListKeyboardNavBlocked } from '../../helpers/list-keyboard-nav.helper';

import {
  DocumentsCellContext,
  DocumentsTableColumnModel,
  getDocumentsTableColumnsGrid,
  getDocumentsTableFillerIndex,
} from './documents-table-column.model';
import { RnplColDirective } from './rnpl-col.directive';
import { TableAlignEnum } from './table-align.enum';
import { MenuPanelComponent } from '../menu-panel/menu-panel.component';
import { MenuPanelItemModel } from '../menu-panel/menu-panel-item.model';

@Component({
  selector: 'rnpl-documents-table',
  templateUrl: './documents-table.component.html',
  styleUrls: ['./documents-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DocumentsTableComponent implements OnChanges, AfterContentInit {

  public alignEnum = TableAlignEnum;

  public columnsGrid: SafeStyle;
  public _columns: DocumentsTableColumnModel[] = [];

  // key -> custom cell template
  public templateMap: { [key: string]: TemplateRef<DocumentsCellContext> } = {};

  @ContentChildren(RnplColDirective, { descendants: true })
  private colTemplates: QueryList<RnplColDirective>;

  @ContentChild('rowDetailTpl', { static: false }) public rowDetailTpl: TemplateRef<{ row: any }>;

  @ViewChild('naturalWidthProbe', { static: true }) public readonly naturalWidthProbeRef: ElementRef<HTMLElement>;

  @ViewChild('headerMenu', { static: false }) private headerMenu: MenuPanelComponent;

  private activeSort: { prop: string; direction: 'ASC' | 'DESC' } | null = null;

  // Initial/current sort state as known by the page (API sort field, e.g. 'updatedAt')
  @Input() defaultSort: { prop: string; direction: 'ASC' | 'DESC' } | null = null;

  @Input() set columns(columns: DocumentsTableColumnModel[]) {
    const cloned = cloneDeep(columns);

    if (this.activeSort) {
      cloned.forEach(c => {
        c.sortDirection = (c.sortable && c.prop === this.activeSort.prop) ? this.activeSort.direction : null;
      });
    }

    this._columns = cloned;
    this.applyDefaultSort();
  }

  private applyDefaultSort(): void {
    if (this.activeSort || !this.defaultSort || !this._columns.length) { return; }

    this._columns.forEach(c => {
      const matchesField = c.prop === this.defaultSort.prop || (!!c.sortProp && c.sortProp === this.defaultSort.prop);
      c.sortDirection = (c.sortable && matchesField) ? this.defaultSort.direction : null;
    });
  }
  @Input() rows: any[] = [];
  @Input() title: string;
  @Input() clickableRows = false;
  @Input() selectable = false;
  @Input() selectedRows: any[] = [];
  @Input() activatedRow: any = null;
  @Input() highlightedRow: any = null;
  @Input() disableKeyboardNav = false;
  @Input() trackByProp: string = null;
  @Input() enableHeaderMenu = false;
  @Input() pagination: PaginationModel = null;
  @Input() getRowCommands: (row: any) => any[] = null;
  // Row click navigates to the row's page: Ctrl/Cmd/Shift + click opens it in a new tab/window instead
  @Input() rowClickOpensDocument = false;

  @Output() rowClickedEmit: EventEmitter<any> = new EventEmitter<any>();
  @Output() selectedRowsChange: EventEmitter<any[]> = new EventEmitter<any[]>();
  @Output() sort: EventEmitter<{ column: DocumentsTableColumnModel; newValue: 'asc' | 'desc' }> = new EventEmitter();
  @Output() rowContextMenu: EventEmitter<{ row: any; event: MouseEvent }> = new EventEmitter();

  get visibleColumns(): DocumentsTableColumnModel[] {
    return this._columns.filter(col => !col.hidden);
  }

  public getRowOrderNumber(index: number): number {
    const page = this.pagination && this.pagination.page;
    const offset = page ? (page - 1) * (this.pagination.per_page || 0) : 0;
    return offset + index + 1;
  }

  public headerMenuItems: MenuPanelItemModel[] = [];
  public headerMenuDraftProp: string = null;
  public headerMenuDraftDirection: 'ASC' | 'DESC' = null;

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (this.disableKeyboardNav || isListKeyboardNavBlocked(event)) { return; }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.selectPrevRow();
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.selectNextRow();
    }
  }

  // public pagination: PaginationModel = { ...DEFAULT_PAGINATION, per_page: 1000 };
  // public sort: FilterModelNew = { sortBy: 'createdAt', direction: DEFAULT_SORT_DIRECTION };

  // readonly destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
    private router: Router,
  ) {
  }

  public onRowAuxClick(event: MouseEvent, row: any): void {
    if (event.button !== 1) { return; }

    if (this.openRowInNewTab(row)) {
      event.preventDefault();
    }
  }

  public onRowClick(event: MouseEvent, row: any): void {
    if (this.hasNewTabModifier(event)) {
      // Links inside the row are opened by the browser itself
      if (this.isLinkTarget(event)) { return; }

      if (this.rowClickOpensDocument) {
        this.openRowInNewTab(row);
        return;
      }
    }

    this.rowClick(row);
  }

  public onRowMouseDown(event: MouseEvent): void {
    // Prevent Shift + click from extending the text selection when it opens a new window
    if (event.button === 0 && event.shiftKey && (this.rowClickOpensDocument || this.isLinkTarget(event))) {
      event.preventDefault();
    }
  }

  private hasNewTabModifier(event: MouseEvent): boolean {
    return event.ctrlKey || event.metaKey || event.shiftKey;
  }

  private isLinkTarget(event: MouseEvent): boolean {
    const target = event.target as HTMLElement;
    return !!(target && target.closest && target.closest('a[href]'));
  }

  private openRowInNewTab(row: any): boolean {
    if (!this.getRowCommands) { return false; }

    const commands = this.getRowCommands(row);
    if (!commands || !commands.length) { return false; }

    const url = this.router.serializeUrl(this.router.createUrlTree(commands));
    window.open(url, '_blank');
    return true;
  }

  ngAfterContentInit(): void {
    this.buildTemplateMap();
    this.colTemplates.changes.subscribe(() => {
      this.buildTemplateMap();
      this.cdr.markForCheck();
    });
  }

  private buildTemplateMap(): void {
    this.templateMap = {};
    this.colTemplates.forEach(t => this.templateMap[t.key] = t.template);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes && changes.hasOwnProperty('defaultSort')) {
      this.applyDefaultSort();
    }

    if (changes && (changes.hasOwnProperty('columns') || changes.hasOwnProperty('selectable'))) {
      this.getColumnsGrid();
      this.getStickyRightOffsets();
    }
  }

  public fillerIndex = -1;

  private getColumnsGrid(): void {
    this.columnsGrid = this.sanitizer.bypassSecurityTrustStyle(
      getDocumentsTableColumnsGrid(this._columns, this.selectable)
    );
    this.fillerIndex = getDocumentsTableFillerIndex(this.visibleColumns);
  }

  public stickyRightOffsets: (string | null)[] = [];

  private getStickyRightOffsets(): void {
    const cols = this.visibleColumns;
    const offsets: (string | null)[] = new Array(cols.length).fill(null);
    let accumulatedPx = 0;

    for (let i = cols.length - 1; i >= 0; i--) {
      if (cols[i].sticky !== 'right') { continue; }
      offsets[i] = `${accumulatedPx}px`;
      const widthPx = parseInt(cols[i].width || '0', 10);
      accumulatedPx += isNaN(widthPx) ? 0 : widthPx;
    }

    this.stickyRightOffsets = offsets;
  }

  public trackByRowId = (index: number, row: any): any => {
    if (!this.trackByProp) { return row; }
    const key = row && row[this.trackByProp];
    return key != null ? key : index;
  }

  public trackByColumn = (index: number, col: DocumentsTableColumnModel): string => {
    return col ? `${col.prop}|${col.cell}|${col.name}` : `${index}`;
  }

  public isRowSelected(row: any): boolean {
    return this.selectedRows.includes(row);
  }

  public get allRowsSelected(): boolean {
    return !!(this.rows && this.rows.length && this.selectedRows && this.selectedRows.length === this.rows.length);
  }

  public toggleRowSelection(row: any, checked: boolean): void {
    const next = checked
      ? [...this.selectedRows, row]
      : this.selectedRows.filter(r => r !== row);
    this.selectedRowsChange.emit(next);
  }

  public toggleAllRows(checked: boolean): void {
    this.selectedRowsChange.emit(checked ? [...this.rows] : []);
  }

  public rowClick(row): void {
    this.rowClickedEmit.emit(row);
  }

  public onRowContextMenu(event: MouseEvent, row: any): void {
    this.rowContextMenu.emit({ row, event });
  }

  public onHeadContextMenu(event: MouseEvent): void {
    if (!this.enableHeaderMenu) { return; }

    event.preventDefault();
    event.stopPropagation();

    if (!this.sortableVisibleColumns.length) { return; }

    this.seedHeaderMenuDraft();
    this.buildHeaderMenuItems();
    this.cdr.markForCheck();

    if (this.headerMenu) {
      this.headerMenu.openAt(event);
    }
  }

  public onHeaderMenuItemSelected(item: MenuPanelItemModel): void {
    if (!item) { return; }

    if (item.id === 'asc' || item.id === 'desc') {
      this.headerMenuDraftDirection = item.id === 'asc' ? 'ASC' : 'DESC';
    } else if (item.id.indexOf('field:') === 0) {
      this.headerMenuDraftProp = item.id.replace('field:', '');
    } else {
      return;
    }

    this.applyHeaderMenuSort();
  }

  private get sortableVisibleColumns(): DocumentsTableColumnModel[] {
    return this.visibleColumns.filter(col => !!col.sortable);
  }

  private seedHeaderMenuDraft(): void {
    if (this.activeSort) {
      this.headerMenuDraftProp = this.activeSort.prop;
      this.headerMenuDraftDirection = this.activeSort.direction;
      return;
    }

    const sortedColumn = this._columns.find(col => !!col.sortDirection);
    this.headerMenuDraftProp = sortedColumn ? sortedColumn.prop : null;
    this.headerMenuDraftDirection = sortedColumn ? sortedColumn.sortDirection : null;
  }

  private buildHeaderMenuItems(): void {
    const currentColumn = this.sortableVisibleColumns.find(col => col.prop === this.headerMenuDraftProp);

    this.headerMenuItems = [
      {
        id: 'sortBy',
        label: 'COMMON.SORT_BY',
        value: currentColumn ? currentColumn.name : null,
        labelMuted: true,
        dividerAfter: true,
        children: this.sortableVisibleColumns.map(col => ({
          id: `field:${col.prop}`,
          label: col.name,
          checked: col.prop === this.headerMenuDraftProp,
        })),
      },
      {
        id: 'desc',
        label: 'COMMON.SORT_DESCENDING',
        icon: 'sort-down',
        checked: this.headerMenuDraftDirection === 'DESC',
        group: true,
      },
      {
        id: 'asc',
        label: 'COMMON.SORT_ASCENDING',
        icon: 'sort-up',
        checked: this.headerMenuDraftDirection === 'ASC',
        group: true,
      },
    ];
  }

  private applyHeaderMenuSort(): void {
    const column = this._columns.find(col => col.prop === this.headerMenuDraftProp);

    if (column) {
      this.sortColumn(column, this.headerMenuDraftDirection);
    }

    if (this.headerMenu) { this.headerMenu.close(); }
  }

  public sortColumn(col: DocumentsTableColumnModel, direction?: 'ASC' | 'DESC'): void {
    if (!col || !col.sortable) { return; }

    const newDirection: 'ASC' | 'DESC' = direction
      ? direction
      : (col.sortDirection === 'ASC' ? 'DESC' : 'ASC');

    this.activeSort = { prop: col.prop, direction: newDirection };

    this._columns.forEach(c => c.sortDirection = c.prop === col.prop ? newDirection : null);

    this.sort.emit({ column: col, newValue: newDirection.toLowerCase() as 'asc' | 'desc' });

    this.cdr.markForCheck();
  }

  public getCellValue(obj: any, path: string): any {
    if (!path) { return null; }

    return path
      .split('.')
      .reduce((o, key) => (o == null ? undefined : o[key]), obj);
  }

  public getCellDisplayValue(row: any, path: string): any {
    const value = this.getCellValue(row, path);
    return (value || value === 0) ? value : '';
  }

  public selectNextRow(): void {
    if (this.rows && this.rows.length && this.activatedRow) {
      const activatedRowIndex =  this.rows.findIndex(r => r === this.activatedRow);

      if (activatedRowIndex < this.rows.length - 1) {
        this.rowClick(this.rows[activatedRowIndex + 1]);
      }
    }
  }

  public selectPrevRow(): void {
    if (this.rows && this.rows.length && this.activatedRow) {
      const activatedRowIndex =  this.rows.findIndex(r => r === this.activatedRow);

      if (activatedRowIndex > 0) {
        this.rowClick(this.rows[activatedRowIndex - 1]);
      }
    }
  }

  // ngOnDestroy() {
  //   this.destroy$.next(true);
  //   this.destroy$.complete();
  // }

}
